import { Injectable } from '@angular/core';
import { first, take } from 'rxjs/operators';
import {
  addFileInBasketAction,
  addFilesInBasketAction,
  changeFileInBasketAction,
  deleteFileInBasketAction,
  deletePrintsAction,
  reconcileProductFilesInBasketAction,
} from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/actions';
import { select, Store } from '@ngrx/store';
import {
  FileInBasket,
  getFirstPrintPriceForPhoto,
  listFilesInProductsInBasket,
  PhotoGroupList,
  PriceToAddToEveryFileInProduct,
  ShoppingCartState,
} from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/standalone';
import {
  selectAllProductsInBasket,
  selectFilePrices,
  selectFilesInBasket,
  selectLookBookProductsInBasket,
  selectPackagesInBasket,
  selectPhotoGroups,
  selectPhotosList,
  selectPriceToAddToEveryFileInLookBookProduct,
  selectPriceToAddToEveryFileInProduct,
  selectPriceToAddToFirstPrint,
  selectPrintsInBasket,
  selectProductsInBasket,
} from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/selector';
import { combineLatest } from 'rxjs';
import {
  PhotoGroup,
  PhotoGroupConfig,
} from 'projects/zalamo-library/src/lib/ui/products/shopping-cart-back/actions/standalone';

type FilterType = 'product' | 'print' | 'lookBookProduct';

@Injectable({
  providedIn: 'root',
})
export class AutoAddFileToBasketTogetherWithProductService {
  filesInBasket$ = this.store.pipe(select(selectFilesInBasket));
  filePrices$ = this.store.pipe(select(selectFilePrices));
  packagesInBasket$ = this.store.pipe(select(selectPackagesInBasket));
  printsInBasket$ = this.store.pipe(select(selectPrintsInBasket));
  productsInBasket$ = this.store.pipe(select(selectProductsInBasket));
  lookBookProductsInBasket$ = this.store.pipe(
    select(selectLookBookProductsInBasket),
  );
  priceToAddToEveryFileInProduct$ = this.store.pipe(
    select(selectPriceToAddToEveryFileInProduct),
  );
  priceToAddToEveryFileInLookBookProduct$ = this.store.pipe(
    select(selectPriceToAddToEveryFileInLookBookProduct),
  );
  photoGroups$ = this.store.pipe(select(selectPhotoGroups));
  photosList$ = this.store.pipe(select(selectPhotosList));

  constructor(private store: Store<{ shoppingCart: ShoppingCartState }>) {}
  findDeletedFileInBasket(
    photoId: string,
    filePricingId: string,
    filterType: FilterType,
  ): void {
    combineLatest([
      this.productsInBasket$,
      this.priceToAddToEveryFileInProduct$,
      this.photosList$,
      this.photoGroups$,
      this.filesInBasket$,
      combineLatest([
        this.filePrices$,
        this.priceToAddToEveryFileInLookBookProduct$,
        this.store.pipe(select(selectPriceToAddToFirstPrint)),
        this.printsInBasket$,
        this.lookBookProductsInBasket$,
      ]),
    ])
      .pipe(take(1))
      .subscribe(
        ([
          productsInBasket,
          priceToAddToEveryFileInProduct,
          photosList,
          photoGroups,
          filesInBasket,
          [
            filePrices,
            priceToAddToEveryFileInLookBookProduct,
            priceToAddToFirstPrint,
            printsInBasket,
            lookBookProductsInBasket,
          ],
        ]) => {
          const listFilesInProduct = listFilesInProductsInBasket(
            filePricingId,
            photoGroups,
            photosList,
            priceToAddToEveryFileInProduct,
            priceToAddToEveryFileInLookBookProduct,
            priceToAddToFirstPrint,
            filePrices,
            printsInBasket,
            productsInBasket,
            lookBookProductsInBasket,
            filesInBasket,
          );

          const remainingTypes = new Set<FilterType>(
            listFilesInProduct
              .filter((x) => x.photoId === photoId && x.type !== filterType)
              .map((x) => x.type as FilterType),
          );

          const photoGroupId =
            photosList
              .find(({ photos }) => photos?.some((p) => p.photoId === photoId))
              ?.photos?.find((p) => p.photoId === photoId)?.photoGroupId ??
            null;

          const config =
            photoGroups.find((g) => g.photoGroupId === photoGroupId)?.config ??
            null;

          const supplementPrint = getFirstPrintPriceForPhoto(
            photoId,
            photoGroups,
            photosList,
            priceToAddToFirstPrint,
          );

          const supplementProduct =
            config?.individualSellSettings &&
            config?.sellProducts &&
            config?.customProductsConfig
              ? config.priceToAddToEveryFileInProduct
              : priceToAddToEveryFileInProduct;

          // Priorytet jak w Twoim pierwowzorze
          if (
            remainingTypes.has('print') &&
            !!supplementPrint?.addPrintFileToBasketIfNotAlreadyBought
          ) {
            this.store.dispatch(
              changeFileInBasketAction({
                filePricingId,
                photoId,
                togetherWithProduct: false,
                togetherWithLookBookProduct: false,
                togetherWithPrint: true,
              }),
            );
            return;
          }

          if (
            remainingTypes.has('product') &&
            !!supplementProduct?.addEveryFileToBasketIfNotAlreadyBought
          ) {
            this.store.dispatch(
              changeFileInBasketAction({
                filePricingId,
                photoId,
                togetherWithProduct: true,
                togetherWithLookBookProduct: false,
                togetherWithPrint: false,
              }),
            );
            return;
          }

          if (
            remainingTypes.has('lookBookProduct') &&
            !!priceToAddToEveryFileInLookBookProduct?.addEveryFileToBasketIfNotAlreadyBought
          ) {
            this.store.dispatch(
              changeFileInBasketAction({
                filePricingId,
                photoId,
                togetherWithProduct: false,
                togetherWithLookBookProduct: true,
                togetherWithPrint: false,
              }),
            );
            return;
          }

          // nic już nie “trzyma” pliku → usuń plik + usuń printy dodane “razem z plikiem”
          this.store.dispatch(deleteFileInBasketAction({ photoId }));

          const printTogetherWithFile = printsInBasket.filter(
            (p) => p.photoId === photoId && !!p.togetherWithFile,
          );

          if (printTogetherWithFile.length) {
            this.store.dispatch(
              deletePrintsAction({ prints: printTogetherWithFile }),
            );
          }
        },
      );
  }
  autoAddFileToBasketTogetherWithProduct(): void {
    this.store.dispatch(reconcileProductFilesInBasketAction());
  }

  autoAddFileToBasketTogetherWithLookBookProduct() {
    combineLatest([
      this.priceToAddToEveryFileInLookBookProduct$,
      this.filesInBasket$,
      this.packagesInBasket$,
      this.lookBookProductsInBasket$,
      this.filePrices$,
    ])
      .pipe(first())
      .subscribe(
        ([
          priceToAddToEveryFileInLookBookProduct,
          filesInBasket,
          packagesInBasket,
          lookBookProductsInBasket,
          filePrices,
        ]) => {
          if (
            priceToAddToEveryFileInLookBookProduct.addPriceToEveryFileInProduct &&
            priceToAddToEveryFileInLookBookProduct.addEveryFileToBasketIfNotAlreadyBought
          ) {
            lookBookProductsInBasket.forEach(({ photoIdList }) => {
              const files = [...photoIdList]
                .filter(
                  (photoId) =>
                    !packagesInBasket.find(({ file }) =>
                      file.find((f) => f.photoId === photoId),
                    ) && !filesInBasket.find((f) => f.photoId === photoId),
                )
                .map((photoId) => {
                  const file = !!filePrices.length
                    ? filePrices.find(({ isFull }) => !!isFull)
                      ? filePrices.find(({ isFull }) => !!isFull)
                      : filePrices[0]
                    : {
                        filePricingId: `togetherWithLookBookProduct`,
                        size: 0,
                        isFull: true,
                      };
                  return {
                    filePricingId: file.filePricingId,
                    photoId,
                    size: file.size,
                    isFull: file.isFull,
                    togetherWithProduct: false,
                    togetherWithLookBookProduct: true,
                    togetherWithPrint: false,
                    retouchLevelId: 'standard',
                  };
                });
              this.store.dispatch(
                addFilesInBasketAction({
                  files,
                }),
              );
            });
          }
          filesInBasket
            .filter(
              ({ togetherWithLookBookProduct, photoId }) =>
                !!togetherWithLookBookProduct &&
                !lookBookProductsInBasket.find(({ photoIdList }) =>
                  photoIdList.find((pId) => pId === photoId),
                ),
            )
            .forEach(({ photoId, filePricingId }) =>
              this.findDeletedFileInBasket(
                photoId,
                filePricingId,
                'lookBookProduct',
              ),
            );
        },
      );
  }

  verificationAddedFile(
    settings: PriceToAddToEveryFileInProduct,
    photoId: string,
    lookBookProduct = false,
  ) {
    if (
      settings.addPriceToEveryFileInProduct &&
      settings.addEveryFileToBasketIfNotAlreadyBought
    ) {
      combineLatest([
        this.packagesInBasket$,
        this.filesInBasket$,
        this.filePrices$,
        this.photoGroups$,
        this.photosList$,
      ])
        .pipe(first())
        .subscribe(
          ([
            packagesInBasket,
            filesInBasket,
            filePrices,
            photoGroups,
            photosList,
          ]) => {
            const photoGroup = photoGroups.find(
              ({ photoGroupId }) =>
                photoGroupId ===
                photosList
                  .find(({ photos }) =>
                    photos.find(({ photoId: id }) => id === photoId),
                  )
                  ?.photos.find(({ photoId: id }) => id === photoId)
                  ?.photoGroupId,
            )?.config;
            const usesIndividualFilePricing =
              photoGroup?.individualSellSettings &&
              photoGroup?.sellFiles &&
              photoGroup?.customFilesPricing;
            const files = usesIndividualFilePricing
              ? photoGroup.files ?? []
              : filePrices;
            if (
              !packagesInBasket.find(({ file }) =>
                file.find((f) => f.photoId === photoId),
              ) &&
              !filesInBasket.find((f) => f.photoId === photoId)
            ) {
              const file = files.find(({ isFull }) => !!isFull) ??
                files[0] ?? {
                  filePricingId: lookBookProduct
                    ? 'togetherWithLookBookProduct'
                    : 'togetherWithProduct',
                  size: 0,
                  isFull: true,
                };
              this.store.dispatch(
                addFileInBasketAction({
                  file: {
                    filePricingId: file.filePricingId,
                    photoId,
                    size: file.size,
                    isFull: file.isFull,
                    togetherWithProduct: !lookBookProduct,
                    togetherWithLookBookProduct: lookBookProduct,
                    togetherWithPrint: false,
                    retouchLevelId: 'standard',
                  },
                }),
              );
            }
          },
        );
    }
  }

  autoAddFileToBasketTogetherWithBasket(): void {
    this.store.dispatch(reconcileProductFilesInBasketAction());
    combineLatest([
      combineLatest([
        this.priceToAddToEveryFileInLookBookProduct$,
        this.store.pipe(select(selectPriceToAddToFirstPrint)),
      ]),
      this.photosList$,
      this.photoGroups$,
      this.store.pipe(select(selectAllProductsInBasket)),
      this.filePrices$,
    ])
      .pipe(first())
      .subscribe(
        ([
          [priceToAddToEveryFileInLookBookProduct, priceToAddToFirstPrint],
          photosList,
          photoGroups,
          allProductsInBasket,
          filePrices,
        ]) => {
          const changeAllProductsInBasket = [...allProductsInBasket];
          allProductsInBasket.forEach((index) => {
            switch (true) {
              case !!index.lookBookProduct:
                {
                  index.lookBookProduct.photoIdList.forEach((photoId) => {
                    const individualConfig = this.findIndividualConfig(
                      photoId,
                      photosList,
                      photoGroups,
                    );
                    const config = individualConfig?.disableLookBook
                      ? {
                          addPriceToEveryFileInProduct: false,
                          addEveryFileToBasketIfNotAlreadyBought: false,
                          skipAddToEveryFileInProductIfFileBought: false,
                          countWithFilePrices: false,
                          price: [],
                        }
                      : priceToAddToEveryFileInLookBookProduct;
                    if (
                      !changeAllProductsInBasket.find((p) =>
                        p?.package?.file.find((f) => f.photoId === photoId),
                      ) ||
                      (!changeAllProductsInBasket.find(
                        (p) => p?.file?.photoId === photoId,
                      ) &&
                        config.addEveryFileToBasketIfNotAlreadyBought)
                    ) {
                      this.verificationAddedFile(config, photoId, true);
                      changeAllProductsInBasket.push({
                        file: this.generateFile(photoId),
                      });
                    }
                  });
                }
                break;
              case !!index.print: {
                const individualConfig = this.findIndividualConfig(
                  index.print.photoId,
                  photosList,
                  photoGroups,
                );
                const config = individualConfig?.disableLookBook
                  ? individualConfig.priceToAddToFirstPrint
                  : priceToAddToFirstPrint;
                if (
                  (!changeAllProductsInBasket.find((p) =>
                    p?.package?.file.find(
                      (f) => f.photoId === index.print.photoId,
                    ),
                  ) ||
                    !changeAllProductsInBasket.find(
                      (p) => p.file?.photoId === index.print.photoId,
                    )) &&
                  config.addPrintFileToBasketIfNotAlreadyBought
                ) {
                  const photoGroup = photoGroups.find(
                    ({ photoGroupId }) =>
                      photoGroupId ===
                      photosList
                        .find(({ photos }) =>
                          photos.find(
                            ({ photoId: id }) => id === index.print.photoId,
                          ),
                        )
                        ?.photos.find(
                          ({ photoId: id }) => id === index.print.photoId,
                        )?.photoGroupId,
                  )?.config;
                  const usesIndividualFilePricing =
                    photoGroup?.individualSellSettings &&
                    photoGroup?.sellFiles &&
                    photoGroup?.customFilesPricing;
                  const files = usesIndividualFilePricing
                    ? photoGroup.files ?? []
                    : filePrices;
                  const file = files.find(({ isFull }) => !!isFull) ??
                    files[0] ?? {
                      filePricingId: `togetherWithPrint`,
                      size: 0,
                      isFull: true,
                    };
                  this.store.dispatch(
                    addFileInBasketAction({
                      file: {
                        filePricingId: file.filePricingId,
                        photoId: index.print.photoId,
                        size: file.size,
                        isFull: file.isFull,
                        togetherWithProduct: false,
                        togetherWithLookBookProduct: false,
                        togetherWithPrint: true,
                        retouchLevelId: 'standard',
                      },
                    }),
                  );
                  changeAllProductsInBasket.push({
                    file: this.generateFile(index.print.photoId),
                  });
                }
              }
            }
          });
        },
      );
  }

  findIndividualConfig(
    photoId: string,
    photosList: Array<PhotoGroupList>,
    photoGroups: Array<PhotoGroup>,
  ): PhotoGroupConfig {
    return photoGroups.find(
      ({ photoGroupId }) =>
        photoGroupId ===
        photosList
          .find(({ photos }) => photos.find((p) => p.photoId === photoId))
          .photos.find((p) => p.photoId === photoId)?.photoGroupId,
    )?.config;
  }

  generateFile(photoId: string): FileInBasket {
    return {
      photoId,
      filePricingId: '',
      size: 0,
      isFull: true,
      retouchLevelId: '',
      timeAddedToShoppingCart: 1,
    };
  }
}
